
import { FC, useState, useEffect } from 'react';

import {useIntl} from 'react-intl'

import {Modal} from '../../../components/Modal'
import { useLang } from '../../../../_metronic/i18n/Metronici18n';
import clipboardCopy from 'clipboard-copy'
import toastr from 'toastr';

import { chatAPI } from '../../../api'

import {SharedChatModel, ChatMessageModel} from '../../../models'
import { Spinner } from 'react-bootstrap';

import { ConfirmModal } from '../../../components/ConfirmModal'


export const APP_URL = window.env?.REACT_APP_URL || process.env.REACT_APP_URL || 'http://localhost:3000'

interface ChatShareProps {
  chatId: string
  lastestChatMessage: ChatMessageModel
  show: boolean
  handleClose: () => void
}
  
export const ChatShareModal: FC<ChatShareProps> = ({ chatId, lastestChatMessage, show, handleClose }) => {
  
  const intl = useIntl()

  const [handling, setHandling] = useState(false)

  const [isLoading, setIsLoading] = useState(false)

  const [sharedChat, setSharedChat] = useState<SharedChatModel | undefined>(undefined)

  const sharedLink = sharedChat ? `${APP_URL}/s/${sharedChat.id}` : ''

  useEffect(() => {
    if (show && chatId) {
      setIsLoading(true)
      chatAPI.getSharedChat(chatId).then(({data: {data}}) => {
        setSharedChat(data)
      }).finally(() => {
        setIsLoading(false)
      })
    }
  }, [chatId, show])

  const handleShare = () => {
    setHandling(true)
    if( sharedChat && sharedChat.id ){
      chatAPI.updateSharing(chatId).then(({data: {data}}) => {
        toastr.info(intl.formatMessage({id: `chat.share.updated`}))
        setSharedChat(data)
      }).finally(() => {
        setHandling(false)
      })
    }else{
      chatAPI.share(chatId).then(({data: {data}}) => {
        setSharedChat(data)
        toastr.info(intl.formatMessage({id: `chat.share.created`}))
      }).finally(() => {
        setHandling(false)
      })
    }
  }

  const handleDelete = () => {
    setHandling(true)
    chatAPI.unshare(chatId).then(() => {
      setSharedChat(undefined)
      toastr.info(intl.formatMessage({id: `chat.deleted`}))
    }).finally(() => {
      setHandling(false)
    })
  }

  const handleCopy = () => {
	  clipboardCopy(sharedLink);
	  toastr.info(intl.formatMessage({id: `chat.message.copied`}))
  }

  return <Modal
    dialogClassName='modal-dialog modal-dialog-centered w-lg-500px'
    show={show}
    handleClose={handleClose}
    title={intl.formatMessage({id: 'chat.share.title'})}
  >
    {isLoading 
    ?
    <div className="text-center">
      <Spinner/>
    </div>
    :
    <>
    <div className="fs-6 text-muted mb-5">
      {intl.formatMessage({id: 'chat.share.description'})}
    </div>
    {sharedChat && sharedChat.id && 
    <div className="mb-5 fv-row">
      <div className="d-flex align-items-center border border-gray-400 rounded p-1" onClick={handleCopy}>
        <input
          type='text'
          autoComplete='off'
          className='form-control form-control-transparent '
          readOnly
          value={sharedLink}
        />
        <button type='button' className='btn btn-clean btn-icon  text-hover-dark'>
          <i className="fa-solid fa-copy fs-5"></i>
        </button>
      </div>
    </div>
    }
    <div className="text-center">
      {(!sharedChat || (sharedChat && lastestChatMessage.created_dt && sharedChat.shared_dt && lastestChatMessage.created_dt > sharedChat.shared_dt)) && 
      <button
        className="btn btn-primary"
        disabled={handling}
        onClick={handleShare}
      >
        {!handling && <span className='indicator-label'>{intl.formatMessage({id: 'chat.share.' + (sharedChat && sharedChat.id ? 'update' : 'create')})}</span>}
        {handling && (
          <span className='indicator-progress' style={{display: 'block'}}>
            {intl.formatMessage({id: `general.please_wait`})}
            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
          </span>
        )}
      </button>
      }
      {sharedChat && sharedChat.id && 
      <button 
        type="button" 
        className="btn btn-danger ms-3" 
        onClick={handleDelete}
      >
        {intl.formatMessage({id: 'chat.share.delete'})}
      </button>
      }
    </div>
    </>}
  </Modal>
}

interface Props {
  chatId: string
  lastestChatMessage: ChatMessageModel
  deleteChat: () => void
  downloadChatMessages: () => void
}
  
const ChatShareOptions: FC<Props> = ({ chatId, lastestChatMessage, deleteChat, downloadChatMessages }) => {

  const intl = useIntl()
  const selectedLang = useLang()

  const [showShareModal, setShowShareModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  
  const handleDelete = () => {
    setShowDeleteModal(false)
    deleteChat()
  }

  return <>
    <div className="btn btn-outline border-gray-400 btn-active-light-dark text-dark me-1" onClick={() => setShowShareModal(true)}>
      <i className="fa-solid fa-share-from-square me-1 text-dark"></i>{intl.formatMessage({id: 'chat.share.button'})}
    </div>
    <div className="btn btn-flex btn-active-light-dark btn-icon" data-kt-menu-trigger="{default: 'click', lg: 'hover'}" data-kt-menu-attach="parent" data-kt-menu-placement={`top-${selectedLang == 'en' ? 'end' : 'start'}`}>
      <i className="fa-solid fa-ellipsis-vertical fs-2"></i>
    </div>
    <div 
      className="chat-menu-dropdown menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg menu-state-color fw-semibold py-2 fs-6 w-150px" 
      data-kt-menu="true"
    >
      <div className="menu-item px-3 my-0">
        <a className={`menu-link px-3 py-2`} onClick={()=> setShowDeleteModal(true)}>
          <span className="menu-icon" data-kt-element="icon">
            <i className={`fa-solid fa-trash fs-5`}></i>
          </span>
          <span className="menu-title">
            {intl.formatMessage({id: 'chat.delete'})}
          </span>
        </a>
      </div>
      <div className="menu-item px-3 my-0">
        <a className={`menu-link px-3 py-2`} onClick={downloadChatMessages}>
          <span className="menu-icon" data-kt-element="icon">
            <i className={`fa-solid fa-file-excel fs-5`}></i>
          </span>
          <span className="menu-title">
            {intl.formatMessage({id: 'chat.download'})}
          </span>
        </a>
      </div>
    </div>
    <ChatShareModal chatId={chatId} show={showShareModal} lastestChatMessage={lastestChatMessage} handleClose={() => setShowShareModal(false)}/>
    <ConfirmModal show={showDeleteModal} handleConfirm={handleDelete} handleClose={() => setShowDeleteModal(false)}/>
  </>
}

export default ChatShareOptions